import {
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { apiRequest } from '../api/client'
import { useAuthStore } from '../stores/auth'
import { useWorkspaceStore } from '../stores/workspace'
import type {
    Client,
    Invoice,
    InvoiceStatus,
    Page,
    Project,
    TimeEntry,
    TimeEntrySummary,
    TimeEntryType,
    UnbilledPreview,
    UserProfile,
    Workspace,
} from '../types/api'

// ---------- Workspaces ----------

export function useCreateWorkspace() {
    const setUser = useAuthStore(s => s.setUser)
    const setWorkspace = useWorkspaceStore(s => s.setWorkspace)
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (name: string) =>
            apiRequest<Workspace>('/workspaces', {
                method: 'POST',
                body: { name },
            }),
        onSuccess: async workspace => {
            const profile = await apiRequest<UserProfile>('/users/profile')
            setUser(profile)
            setWorkspace(workspace.id)
            queryClient.removeQueries()
        },
    })
}

// ---------- Clients ----------

export function useClients(workspaceId: number | null) {
    return useQuery({
        queryKey: ['clients', workspaceId],
        enabled: workspaceId !== null,
        queryFn: () =>
            apiRequest<Client[]>(
                `/clients?workspaceId=${workspaceId}`,
            ),
    })
}

export function useClientMutations(workspaceId: number | null) {
    const queryClient = useQueryClient()
    const invalidate = () =>
        queryClient.invalidateQueries({ queryKey: ['clients', workspaceId] })

    const create = useMutation({
        mutationFn: (body: {
            name: string
            contactDetails: string
            notes: string
            address?: string
            vatId?: string
        }) =>
            apiRequest<Client>('/clients', {
                method: 'POST',
                body: { workspaceId, ...body },
            }),
        onSuccess: invalidate,
    })

    const update = useMutation({
        mutationFn: ({
                id,
                ...body
            }: {
                id: number
                name?: string
                contactDetails?: string
                notes?: string
                address?: string | null
                vatId?: string | null
            }) =>
            apiRequest<Client>(`/clients/${id}`, {
                method: 'PATCH',
                body,
            }),
        onSuccess: invalidate,
    })

    const remove = useMutation({
        mutationFn: (id: number) =>
            apiRequest<void>(`/clients/${id}`, { method: 'DELETE' }),
        onSuccess: invalidate,
    })

    return { create, update, remove }
}

// ---------- Projects ----------

export function useProjects(workspaceId: number | null) {
    return useQuery({
        queryKey: ['projects', workspaceId],
        enabled: workspaceId !== null,
        queryFn: async () => {
            const page = await apiRequest<Page<Project>>(
                `/projects?workspaceId=${workspaceId}&limit=100`,
            )
            return page.data
        },
    })
}

export function useProjectMutations(workspaceId: number | null) {
    const queryClient = useQueryClient()
    const invalidate = () => {
        void queryClient.invalidateQueries({ queryKey: ['projects', workspaceId] })
        void queryClient.invalidateQueries({ queryKey: ['clients', workspaceId] })
    }

    const create = useMutation({
        mutationFn: (body: {
            clientId: number
            name: string
            notes: string
            hourlyRate?: number | null
        }) => apiRequest<Project>('/projects', { method: 'POST', body }),
        onSuccess: invalidate,
    })

    const update = useMutation({
        mutationFn: ({
                id,
                ...body
            }: {
                id: number
                name?: string
                notes?: string
                hourlyRate?: number | null
            }) =>
            apiRequest<Project>(`/projects/${id}`, {
                method: 'PATCH',
                body,
            }),
        onSuccess: invalidate,
    })

    const remove = useMutation({
        mutationFn: (id: number) =>
            apiRequest<void>(`/projects/${id}`, { method: 'DELETE' }),
        onSuccess: invalidate,
    })

    return { create, update, remove }
}

// ---------- Time entries ----------

export interface EntryFilters {
    userId?: number
    projectId?: number
    clientId?: number
    entryType?: TimeEntryType
    startDate?: string
    endDate?: string
    page?: number
    limit?: number
}

export function entryQueryString(
    workspaceId: number,
    filters: EntryFilters = {},
): string {
    const params = new URLSearchParams({ workspaceId: String(workspaceId) })
    if (filters.userId) params.set('userId', String(filters.userId))
    if (filters.projectId) params.set('projectId', String(filters.projectId))
    if (filters.clientId) params.set('clientId', String(filters.clientId))
    if (filters.entryType) params.set('entryType', filters.entryType)
    if (filters.startDate) params.set('startDate', filters.startDate)
    if (filters.endDate) params.set('endDate', filters.endDate)
    params.set('page', String(filters.page ?? 1))
    params.set('limit', String(filters.limit ?? 25))
    return params.toString()
}

export function useTimeEntries(workspaceId: number | null, filters: EntryFilters = {}) {
    return useQuery({
        queryKey: ['entries', workspaceId, filters],
        enabled: workspaceId !== null,
        queryFn: () =>
            apiRequest<Page<TimeEntry>>(
                `/time-entries?${entryQueryString(workspaceId!, filters)}`,
            ),
    })
}

/**
 * Alle Einträge eines Zeitraums in einem Array — seitenweise geladen,
 * weil das Backend limit auf 100 pro Seite deckelt. Query-Key beginnt
 * mit ['entries', workspaceId], damit die Entry-Mutationen (create/
 * update/remove) diesen Cache mitinvalidieren.
 */
export function useTimeEntriesAll(workspaceId: number | null, filters: EntryFilters = {}) {
    return useQuery({
        queryKey: ['entries', workspaceId, 'all', filters],
        enabled: workspaceId !== null,
        queryFn: async () => {
            const pageFilters = { ...filters, page: 1, limit: 100 }
            const first = await apiRequest<Page<TimeEntry>>(
                `/time-entries?${entryQueryString(workspaceId!, pageFilters)}`,
            )
            const data = [...first.data]
            for (
                let page = 2;
                page <= first.meta.totalPages && page <= 10;
                page++
            ) {
                const next = await apiRequest<Page<TimeEntry>>(
                    `/time-entries?${entryQueryString(workspaceId!, {
                        ...pageFilters,
                        page,
                    })}`,
                )
                data.push(...next.data)
            }
            return data
        },
    })
}

export type SummaryGroupBy = 'none' | 'project' | 'client' | 'user' | 'day'

export interface SummaryFilters {
    startDate?: string
    endDate?: string
    clientId?: number
    projectId?: number
    groupBy?: SummaryGroupBy
}

export function summaryQueryString(
    workspaceId: number,
    filters: SummaryFilters = {},
): string {
    const params = new URLSearchParams({ workspaceId: String(workspaceId) })
    if (filters.startDate) params.set('startDate', filters.startDate)
    if (filters.endDate) params.set('endDate', filters.endDate)
    if (filters.clientId) params.set('clientId', String(filters.clientId))
    if (filters.projectId) params.set('projectId', String(filters.projectId))
    if (filters.groupBy) params.set('groupBy', filters.groupBy)
    return params.toString()
}

export function useSummary(
    workspaceId: number | null,
    filters: SummaryFilters = {},
) {
    return useQuery({
        queryKey: ['summary', workspaceId, filters],
        enabled: workspaceId !== null,
        queryFn: () =>
            apiRequest<TimeEntrySummary>(
                `/time-entries/summary?${summaryQueryString(workspaceId!, filters)}`,
            ),
    })
}

export function useEntryMutations(workspaceId: number | null) {
    const queryClient = useQueryClient()
    const invalidate = () => {
        void queryClient.invalidateQueries({ queryKey: ['entries', workspaceId] })
        void queryClient.invalidateQueries({ queryKey: ['summary', workspaceId] })
        void queryClient.invalidateQueries({ queryKey: ['invoices', workspaceId] })
        void queryClient.invalidateQueries({ queryKey: ['unbilled', workspaceId] })
        void queryClient.invalidateQueries({ queryKey: ['workingTimeMonthly', workspaceId] })
    }

    const create = useMutation({
        mutationFn: (body: {
            description: string
            projectId?: number
            clientId?: number
            entryType: TimeEntryType
            isBillable: boolean
            startTime: string
            endTime?: string
            hourlyRate?: number
            /** OWNER/ADMIN: Eintrag für ein anderes Mitglied (sonst selbst) */
            userId?: number
        }) =>
            apiRequest<TimeEntry>('/time-entries', {
                method: 'POST',
                body: { workspaceId, ...body },
            }),
        onSuccess: invalidate,
    })

    const update = useMutation({
        mutationFn: ({
                id,
                ...body
            }: {
                id: number
                description?: string
                projectId?: number | null
                clientId?: number | null
                entryType?: TimeEntryType
                isBillable?: boolean
                startTime?: string
                endTime?: string | null
                hourlyRate?: number | null
            }) =>
            apiRequest<TimeEntry>(`/time-entries/${id}`, {
                method: 'PATCH',
                body,
            }),
        onSuccess: invalidate,
    })

    const remove = useMutation({
        mutationFn: (id: number) =>
            apiRequest<void>(`/time-entries/${id}`, { method: 'DELETE' }),
        onSuccess: invalidate,
    })

    return { create, update, remove }
}

// ---------- Invoices ----------

export function useInvoices(
    workspaceId: number | null,
    status?: InvoiceStatus,
    page = 1,
) {
    const params = new URLSearchParams({
        workspaceId: String(workspaceId),
        page: String(page),
        limit: '20',
    })
    if (status) params.set('status', status)
    return useQuery({
        queryKey: ['invoices', workspaceId, status ?? 'ALL', page],
        enabled: workspaceId !== null,
        queryFn: () =>
            apiRequest<Page<Invoice>>(`/invoices?${params.toString()}`),
    })
}

export function useInvoice(id: number | null) {
    return useQuery({
        queryKey: ['invoice', id],
        enabled: id !== null,
        queryFn: () => apiRequest<Invoice>(`/invoices/${id}`),
    })
}

export function useUnbilled(
    workspaceId: number | null,
    clientId: number | null,
    from?: string,
    to?: string,
) {
    return useQuery({
        queryKey: [
            'unbilled',
            workspaceId,
            clientId,
            from ?? null,
            to ?? null,
        ],
        enabled: workspaceId !== null && clientId !== null,
        queryFn: () => {
            const params = new URLSearchParams({
                workspaceId: String(workspaceId),
                clientId: String(clientId),
            })
            if (from) params.set('from', from)
            if (to) params.set('to', to)
            return apiRequest<UnbilledPreview>(
                `/invoices/unbilled?${params.toString()}`,
            )
        },
    })
}

export function useInvoiceMutations(workspaceId: number | null) {
    const queryClient = useQueryClient()
    const invalidate = () => {
        void queryClient.invalidateQueries({ queryKey: ['invoices', workspaceId] })
        void queryClient.invalidateQueries({ queryKey: ['unbilled', workspaceId] })
        void queryClient.invalidateQueries({ queryKey: ['entries', workspaceId] })
        void queryClient.invalidateQueries({ queryKey: ['summary', workspaceId] })
    }

    const create = useMutation({
        mutationFn: (body: {
            clientId: number
            from?: string
            to?: string
            taxRate?: number
            issueDate?: string
            dueDate?: string | null
            note?: string
        }) =>
            apiRequest<Invoice>('/invoices', {
                method: 'POST',
                body: { workspaceId, ...body },
            }),
        onSuccess: invalidate,
    })

    /** Nur DRAFT-Rechnungen; taxAmount/total werden bei taxRate-Änderung neu berechnet. */
    const update = useMutation({
        mutationFn: ({
            id,
            ...body
        }: {
            id: number
            issueDate?: string
            dueDate?: string | null
            taxRate?: number
            note?: string | null
        }) =>
            apiRequest<Invoice>(`/invoices/${id}`, {
                method: 'PATCH',
                body,
            }),
        onSuccess: (_inv, variables) => {
            invalidate()
            void queryClient.invalidateQueries({
                queryKey: ['invoice', variables.id],
            })
        },
    })

    const updateStatus = useMutation({
        mutationFn: ({ id, status }: { id: number; status: InvoiceStatus }) =>
            apiRequest<Invoice>(`/invoices/${id}/status`, {
                method: 'PATCH',
                body: { status },
            }),
        onSuccess: invalidate,
    })

    const remove = useMutation({
        mutationFn: (id: number) =>
            apiRequest<void>(`/invoices/${id}`, { method: 'DELETE' }),
        onSuccess: invalidate,
    })

    return { create, update, updateStatus, remove }
}
